import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useRoleTabs } from '../../context/PermissionContext';
import useWaitingTimePredictor, { formatWaitTime, confidenceColor } from '../../hooks/useWaitingTimePredictor';
import useAssignableProviders, { DOCTORS_LOAD_ERROR_COPY } from '../../hooks/useAssignableProviders';
import { STAFF_ROLES } from '../../constants/staff';
import VisitActionsPanel from '../visit/VisitActionsPanel';
import VisitJourney from '../visit/VisitJourney';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import { formatToken, isAppointmentVisit } from '../../utils/formatToken';
import NextPatientBanner from '../common/NextPatientBanner/NextPatientBanner';
import { PhoneButton } from '../common/PhoneButton';
import useAppointmentStore from '../../stores/appointmentStore';
import { useScope } from '../../context/PermissionContext';
import ScopeCaption, { useDeskWallViewer } from '../common/ScopeCaption';
import ReceptionistTaskWidget from '../procedures/widget/ReceptionistTaskWidget';
import useSittingStore from '../../stores/sittingStore';
import { listMyActionPending } from '../../services/api/visitActionPendingApi';
import {
  patientName, StatPill,
  orderTabsByPatterns,
  CancelConfirmDialog, EmptyState,
} from './WorkflowShared';

const INITIAL_VISIBLE = 5;
const LOAD_MORE_STEP = 5;

// ReceptionistView — Queue management for receptionists
export default function ReceptionistView({ queue, inProgress, skipped, dailyVisits, consultationStats, uniqueDoctors, doctors, isDark, onOpenRegister, onNavigate, onNavigateToAppointments, updateVisit, skipVisit, requeueVisit, role }) {
  const { memberId } = useScope();
  const deskWalled = useDeskWallViewer();
  // Pre-warm action-pending data (sittings + parked visits) so the pill
  // renders with a real merged count on first paint instead of waiting
  // for hover. The widget also fetches on its own mount as a fallback
  // for non-dashboard surfaces.
  const fetchActionPendingSittings = useSittingStore((s) => s.fetchActionPendingSittings);
  useEffect(() => {
    if (typeof fetchActionPendingSittings === 'function') {
      fetchActionPendingSittings().catch(() => {});
    }
    listMyActionPending().catch(() => {});
  }, [fetchActionPendingSittings]);
  const [doctorFilter, setDoctorFilter] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loadMoreClicks, setLoadMoreClicks] = useState(0);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  const { logAction: logWorkflow, patterns, fetchPatterns } = useWorkflowStore();

  useEffect(() => {
    if (!patterns) fetchPatterns();
  }, [patterns, fetchPatterns]);

  // ── Prediction engine ────────────────────────────────────────────
  const predictor = useWaitingTimePredictor(dailyVisits || [], consultationStats);

  // ── Filtered queue (+ in-progress when searching) ─────────────
  const filteredQueue = useMemo(() => {
    const isSearching = patientSearch.trim().length > 0;
    let result = isSearching ? [...(queue || []), ...(inProgress || [])] : (queue || []);

    if (doctorFilter) {
      result = result.filter(
        (v) => (v.doctor_member_id || v.visit_doctor_id) === doctorFilter
      );
    }

    if (isSearching) {
      const q = patientSearch.toLowerCase().trim();
      result = result.filter(
        (v) =>
          patientName(v).toLowerCase().includes(q) ||
          (v.patient_mobile || '').includes(q) ||
          String(v.queue_number || '').includes(q)
      );
    }

    return result;
  }, [queue, inProgress, doctorFilter, patientSearch]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
    setLoadMoreClicks(0);
  }, [doctorFilter, patientSearch]);

  // ── Doctor summary with predictions ──────────────────────────────
  // S10-AC2 — a doctor CARD (which is also the doctor filter) is offered only
  // for doctors this viewer may pick: the server-scoped assignable list. While
  // it loads (or fails) there are no cards — never the org-wide set. A visit
  // row's doctor NAME is NOT gated by this: it comes from the visit itself
  // (org-wide), so an Emergency visit under a doctor outside the viewer's
  // departments still names its doctor — it just gets no card.
  const { providers: assignableDoctors, error: assignableDoctorsError } = useAssignableProviders(STAFF_ROLES.DOCTOR);
  const assignableDoctorIds = useMemo(
    () => new Set(assignableDoctors.map((p) => p.member_id)),
    [assignableDoctors],
  );
  const doctorSummaries = useMemo(() => {
    return (uniqueDoctors || []).filter((doc) => assignableDoctorIds.has(doc.id)).map((doc) => {
      const prediction = predictor.getDoctorPrediction(doc.id);
      const docQueue = (queue || []).filter(
        (v) => (v.doctor_member_id || v.visit_doctor_id) === doc.id
      );
      const docActive = (inProgress || []).filter(
        (v) => (v.doctor_member_id || v.visit_doctor_id) === doc.id
      );
      return {
        ...doc,
        queueCount: docQueue.length,
        activeCount: docActive.length,
        prediction,
      };
    });
  }, [uniqueDoctors, assignableDoctorIds, queue, inProgress, predictor]);

  // The list failed to load while today's visits DO have doctors: say so under
  // the heading (never fall back to org-wide / visit-derived cards). No text
  // while loading (cached list — a blink) or when there is nobody to show.
  const doctorCardsLoadFailed = !!assignableDoctorsError && (uniqueDoctors || []).length > 0;

  // ── Load more / navigate handler ─────────────────────────────────
  const handleLoadMore = useCallback(() => {
    const newClicks = loadMoreClicks + 1;
    if (newClicks >= 2 && onNavigate) {
      onNavigate(null);
      return;
    }
    setLoadMoreClicks(newClicks);
    setVisibleCount((prev) => prev + LOAD_MORE_STEP);
  }, [loadMoreClicks, onNavigate]);

  // Receptionist tabs — derived from central permissions (only report)
  const RECEP_TABS = useRoleTabs();
  const orderedRecepTabs = useMemo(() => orderTabsByPatterns(RECEP_TABS, patterns), [RECEP_TABS, patterns]);

  // Cancel visit handler
  const handleCancelVisit = useCallback((visitId) => {
    const allVisits = [...(queue || []), ...(inProgress || []), ...(skipped || [])];
    const visit = allVisits.find((v) => v.visit_id === visitId);
    const name = visit ? patientName(visit) : 'this patient';
    setCancelConfirm({ visitId, name });
  }, [queue, inProgress, skipped]);

  const confirmCancelVisit = useCallback(async (comment) => {
    if (!cancelConfirm) return;
    const { visitId } = cancelConfirm;
    setCancelConfirm(null);
    setActiveTab(null);
    await updateVisit(visitId, { status: 'CANCELLED', cancellation_reason: comment, cancelled_by_role: role });
    logWorkflow(visitId, WORKFLOW_ACTIONS.MARK_CANCELLED);
  }, [cancelConfirm, updateVisit, logWorkflow, role]);

  const handleToggleCard = useCallback((visitId) => {
    setExpandedVisit((prev) => {
      if (prev === visitId) { setActiveTab(null); return null; }
      setActiveTab(null);
      return visitId;
    });
  }, []);

  const handleTabToggle = useCallback((key) => {
    setActiveTab((prev) => (prev === key ? null : key));
  }, []);

  // ── Filtered skipped (respects doctor filter + search) ────────────
  const filteredSkipped = useMemo(() => {
    let result = skipped || [];
    if (doctorFilter) {
      result = result.filter(
        (v) => (v.doctor_member_id || v.visit_doctor_id) === doctorFilter
      );
    }
    if (patientSearch.trim()) {
      const q = patientSearch.toLowerCase().trim();
      result = result.filter(
        (v) =>
          patientName(v).toLowerCase().includes(q) ||
          (v.patient_mobile || '').includes(q) ||
          String(v.queue_number || '').includes(q)
      );
    }
    return result;
  }, [skipped, doctorFilter, patientSearch]);

  const visiblePatients = filteredQueue.slice(0, visibleCount);
  const remainingCount = filteredQueue.length - visibleCount;

  // ── Filter input styles ──────────────────────────────────────────
  const filterInputStyle = {
    padding: '0.375rem 0.5rem',
    borderRadius: '0.5rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.75rem',
    outline: 'none',
    width: '100%',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Action-pending pill on the right edge — merged sittings + parked visits */}
      <ReceptionistTaskWidget viewer={{ memberId, role }} />

      {/* S13 — names the walled receptionist's scope (visit wall). */}
      <ScopeCaption scoped={deskWalled} />

      {/* ── Live counts ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <StatPill label="In Queue" value={queue.length} color="#3b82f6" isDark={isDark} />
        <StatPill label="With Doctor" value={inProgress.length} color="#8b5cf6" isDark={isDark} />
        <StatPill label="Completed" value={(dailyVisits || []).filter((v) => v.status === 'COMPLETED').length} color="#4ade80" isDark={isDark} />
        {(skipped || []).length > 0 && (
          <StatPill label="Skipped" value={skipped.length} color="#f59e0b" isDark={isDark} />
        )}
      </div>

      {/* ── Next Patient Banners (per-doctor) — sticky until resolved ── */}
      <NextPatientBanner
        isDark={isDark}
        dailyVisits={dailyVisits}
        onSkip={(visitId) => skipVisit?.(visitId, role)}
        onCancel={(visitId) => handleCancelVisit(visitId)}
      />

      {/* ── Register CTA ──────────────────────────────────────────── */}
      {onOpenRegister && (
        <button
          onClick={onOpenRegister}
          style={{
            width: '100%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1rem',
            borderRadius: '0.75rem',
            border: 'none',
            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 600,
            transition: 'background 0.2s ease, opacity 0.2s ease',
          }}
        >
          📝 Register New Visit
        </button>
      )}

      {/* ── Today's Appointments (Receptionist compact view) ──────── */}
      <TodayAppointmentsSection isDark={isDark} onNavigateToAppointments={onNavigateToAppointments} />

      {/* ── Doctor-wise summary cards ─────────────────────────────── */}
      {(doctorSummaries.length > 0 || doctorCardsLoadFailed) && (
        <div>
          <p style={{ margin: '0 0 0.375rem', fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#6b7280' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Doctor Queues
          </p>
          {doctorCardsLoadFailed && (
            <p data-testid="doctor-cards-error" style={{ margin: 0, fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
              {DOCTORS_LOAD_ERROR_COPY}
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.375rem' }}>
            {doctorSummaries.map((doc) => {
              const isSelected = doctorFilter === doc.id;
              const pred = doc.prediction;
              return (
                <button
                  key={doc.id}
                  onClick={() => setDoctorFilter(isSelected ? '' : doc.id)}
                  style={{
                    cursor: 'pointer',
                    padding: '0.5rem 0.625rem',
                    borderRadius: '0.5rem',
                    border: `1px solid ${isSelected ? (isDark ? '#3b82f680' : '#3b82f640') : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    background: isSelected
                      ? isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)'
                      : isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                    textAlign: 'left',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                >
                  {(() => {
                    const docProfile = (doctors || []).find((d) => (d.member_id || d.doctor_id) === doc.id);
                    const avail = docProfile?.availability || 'available';
                    const availConfig = { available: { icon: '🟢', label: 'Available' }, on_hold: { icon: '🟡', label: 'On Hold' }, closed_today: { icon: '🔴', label: 'Closed' } }[avail];
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.375rem' }}>
                        <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          🩺 Dr. {doc.name}
                        </p>
                        {avail !== 'available' && (
                          <span style={{ fontSize: '0.5625rem', whiteSpace: 'nowrap', color: avail === 'on_hold' ? '#f59e0b' : '#ef4444' }}>
                            {availConfig?.icon} {availConfig?.label}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', fontSize: '0.625rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    <span>{doc.queueCount} waiting</span>
                    {doc.activeCount > 0 && <span>· {doc.activeCount} active</span>}
                  </div>
                  {pred && pred.queueLength > 0 && (
                    <div style={{ marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.5625rem', color: confidenceColor(pred.confidence, isDark) }}>
                        ⏱ ~{formatWaitTime(pred.queueWaitMinutes)} wait
                      </span>
                      <span style={{ fontSize: '0.5rem', color: isDark ? '#4b5563' : '#d1d5db' }}>
                        · avg {formatWaitTime(pred.avgMinutes)}/pt
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filters: search ───────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            placeholder="🔍 Search patient name, mobile, or #queue…"
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
            style={filterInputStyle}
          />
          {patientSearch && (
            <button
              onClick={() => setPatientSearch('')}
              style={{
                position: 'absolute',
                right: '0.375rem',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                fontSize: '0.625rem',
                color: isDark ? '#6b7280' : '#9ca3af',
                padding: '0.125rem 0.25rem',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          )}
        </div>
        {doctorFilter && (
          <button
            onClick={() => setDoctorFilter('')}
            style={{
              cursor: 'pointer',
              padding: '0.375rem 0.5rem',
              borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)',
              fontSize: '0.625rem',
              color: '#3b82f6',
              whiteSpace: 'nowrap',
            }}
          >
            ✕ {uniqueDoctors.find((d) => d.id === doctorFilter)?.name || 'Filter'}
          </button>
        )}
      </div>

      {/* ── Queue patient list with waiting times — expandable ─────── */}
      {filteredQueue.length === 0 ? (
        <EmptyState
          isDark={isDark}
          icon={patientSearch || doctorFilter ? '🔍' : '✅'}
          title={patientSearch || doctorFilter ? 'No matching patients' : 'Queue empty'}
          subtitle={patientSearch || doctorFilter ? 'Try a different search' : 'No patients in queue right now'}
        />
      ) : (
        <div>
          <p style={{ margin: '0 0 0.375rem', fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#6b7280' : '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Waiting Patients ({filteredQueue.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {visiblePatients.map((v) => {
              const waitInfo = predictor.getPatientWaitTime(v.visit_id);
              const isExpanded = expandedVisit === v.visit_id;
              return (
                <div
                  key={v.visit_id}
                  style={{
                    borderRadius: '0.5rem',
                    border: `1px solid ${isExpanded
                      ? isDark ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.15)'
                      : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
                    background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
                    overflow: 'hidden',
                    transition: 'border-color 0.15s ease',
                  }}
                >
                  {/* Card header — click to expand */}
                  <button
                    onClick={() => handleToggleCard(v.visit_id)}
                    style={{
                      width: '100%', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.5rem 0.625rem',
                      background: 'transparent', border: 'none', textAlign: 'left',
                    }}
                  >
                    {/* Queue number badge */}
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '1.5rem',
                        height: '1.5rem',
                        borderRadius: '0.375rem',
                        background: v.status === 'IN_PROGRESS'
                          ? isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)'
                          : isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        color: v.status === 'IN_PROGRESS' ? '#8b5cf6' : '#3b82f6',
                        flexShrink: 0,
                      }}
                    >
                      {formatToken(v.queue_number, v.appointment_number, { hash: false }) || '·'}
                    </span>

                    {/* Patient info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {patientName(v)}
                        </p>
                        <PhoneButton phoneNumber={v.patient_mobile} isDark={isDark} size="sm" />
                        {isAppointmentVisit(v) && <span style={{ fontSize: '0.5rem', padding: '0.0625rem 0.25rem', borderRadius: '0.1875rem', background: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)', color: '#a855f7', fontWeight: 600, flexShrink: 0 }}>📅</span>}
                      </div>
                      <p style={{ margin: '0.0625rem 0 0', fontSize: '0.625rem', color: isDark ? '#6b7280' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.doctor_name ? `Dr. ${v.doctor_name}` : ''}
                        {v.visit_type ? ` · ${v.visit_type}` : ''}
                      </p>
                    </div>

                    {/* Status / wait time */}
                    {v.status === 'IN_PROGRESS' ? (
                      <span style={{
                        fontSize: '0.5625rem',
                        fontWeight: 600,
                        color: '#8b5cf6',
                        background: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.25rem',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                        With Doctor
                      </span>
                    ) : waitInfo && (
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{
                          margin: 0,
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          color: confidenceColor(waitInfo.confidence, isDark),
                        }}>
                          ~{formatWaitTime(waitInfo.waitMinutes)}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.5625rem', color: isDark ? '#4b5563' : '#d1d5db' }}>
                          {waitInfo.patientsAhead} ahead
                        </p>
                      </div>
                    )}

                    <span style={{ fontSize: '0.5rem', color: isDark ? '#6b7280' : '#9ca3af', flexShrink: 0 }}>
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </button>

                  {/* Expanded — inline tab bar + panel */}
                  {isExpanded && (
                    <div style={{ padding: '0 0.625rem 0.5rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                      {/* Phase 3.2/3.3 — patient journey: headline + narrative
                          segments. When this receptionist holds the overlay,
                          they get an inline "Resolve →" affordance. */}
                      <div style={{ paddingTop: '0.375rem' }}>
                        <VisitJourney
                          visitId={v.visit_id}
                          viewerMemberId={memberId}
                          viewerRole="RECEPTIONIST"
                          compact
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '0.3125rem', flexWrap: 'wrap', padding: '0.375rem 0 0.25rem' }}>
                        {orderedRecepTabs.map((tab) => {
                          const isActive = activeTab === tab.key;
                          return (
                            <button
                              key={tab.key}
                              onClick={() => handleTabToggle(tab.key)}
                              style={{
                                cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                padding: '0.3125rem 0.5rem', borderRadius: '0.375rem',
                                fontSize: '0.625rem', fontWeight: isActive ? 600 : 400,
                                border: isActive
                                  ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                                  : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                                background: isActive
                                  ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
                                  : 'transparent',
                                color: isActive ? '#3b82f6' : isDark ? '#d4d4d8' : '#374151',
                                transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                              }}
                            >
                              <span>{tab.icon}</span>
                              <span>{tab.label}</span>
                              {isActive && <span style={{ fontSize: '0.4375rem', marginLeft: '0.0625rem', opacity: 0.7 }}>▲</span>}
                            </button>
                          );
                        })}

                        {/* Cancel button — for QUEUE visits */}
                        {v.status === 'QUEUE' && (
                          <button
                            onClick={() => handleCancelVisit(v.visit_id)}
                            style={{
                              cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.3125rem 0.5rem', borderRadius: '0.375rem',
                              fontSize: '0.625rem', fontWeight: 500,
                              border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)'}`,
                              background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)',
                              color: '#ef4444',
                              transition: 'background 0.15s, opacity 0.15s',
                            }}
                          >
                            <span>✕</span>
                            <span>Cancel</span>
                          </button>
                        )}

                        {/* Skip button — for QUEUE and IN_PROGRESS visits */}
                        {(v.status === 'QUEUE' || v.status === 'IN_PROGRESS') && (
                          <button
                            onClick={() => skipVisit?.(v.visit_id, role)}
                            style={{
                              cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.3125rem 0.5rem', borderRadius: '0.375rem',
                              fontSize: '0.625rem', fontWeight: 500,
                              border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
                              background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.06)',
                              color: isDark ? '#f59e0b' : '#d97706',
                              transition: 'background 0.15s, opacity 0.15s',
                            }}
                          >
                            <span>⏭</span>
                            <span>Skip</span>
                          </button>
                        )}

                        {/* Quick navigate to full manage page */}
                        <button
                          onClick={() => onNavigate?.(v.visit_id)}
                          style={{
                            cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            padding: '0.3125rem 0.5rem', borderRadius: '0.375rem',
                            fontSize: '0.625rem', fontWeight: 400,
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`,
                            background: 'transparent',
                            color: isDark ? '#9ca3af' : '#6b7280',
                            marginLeft: 'auto',
                            transition: 'background 0.15s, opacity 0.15s',
                          }}
                        >
                          <span>📄</span>
                          <span>Full Details →</span>
                        </button>
                      </div>

                      {activeTab && (
                        <VisitActionsPanel
                          visit={v}
                          userRole="RECEPTIONIST"
                          initialTab={activeTab}
                          hideActionBar
                          onClose={() => setActiveTab(null)}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Load more / navigate to queue */}
          {remainingCount > 0 && (
            <button
              onClick={handleLoadMore}
              style={{
                width: '100%',
                marginTop: '0.375rem',
                cursor: 'pointer',
                padding: '0.375rem 0.5rem',
                borderRadius: '0.5rem',
                border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                background: 'transparent',
                fontSize: '0.6875rem',
                color: isDark ? '#9ca3af' : '#6b7280',
                textAlign: 'center',
                transition: 'background 0.15s, opacity 0.15s',
              }}
            >
              {loadMoreClicks >= 1
                ? `View all ${filteredQueue.length} in Manage Queue →`
                : `Load more (${remainingCount} remaining)`}
            </button>
          )}
        </div>
      )}

      {/* ── Skipped Patients Section ──────────────────────────────── */}
      {filteredSkipped.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <p style={{
            margin: '0 0 0.375rem',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: isDark ? '#f59e0b' : '#d97706',
            display: 'flex', alignItems: 'center', gap: '0.375rem',
          }}>
            <span>⏭️</span> Skipped ({filteredSkipped.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {filteredSkipped.map((v) => (
              <div
                key={v.visit_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.625rem',
                  borderRadius: '0.625rem',
                  border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.15)'}`,
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(217,119,6,0.03))'
                    : 'linear-gradient(135deg, rgba(245,158,11,0.04), rgba(217,119,6,0.02))',
                }}
              >
                {/* Queue badge */}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: '1.5rem', height: '1.5rem', borderRadius: '0.375rem',
                  background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)',
                  fontSize: '0.625rem', fontWeight: 700, color: isDark ? '#f59e0b' : '#d97706',
                }}>
                  {v.queue_number ? `#${v.queue_number}` : '·'}
                </span>

                {/* Name + skipped label */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <p style={{
                      margin: 0, fontSize: '0.75rem', fontWeight: 500,
                      color: isDark ? '#e5e7eb' : '#374151',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {patientName(v)}
                    </p>
                    <PhoneButton phoneNumber={v.patient_mobile} isDark={isDark} size="sm" />
                  </div>
                  <p style={{ margin: '0.0625rem 0 0', fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
                    Skipped{v.skipped_at ? ` at ${new Date(v.skipped_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </p>
                </div>

                {/* Cancel button — for skipped visits */}
                <button
                  onClick={() => handleCancelVisit(v.visit_id)}
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.3125rem 0.625rem', borderRadius: '0.375rem',
                    fontSize: '0.625rem', fontWeight: 500,
                    border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)'}`,
                    background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)',
                    color: '#ef4444',
                    transition: 'background 0.15s',
                  }}
                >
                  <span>✕</span>
                  <span>Cancel</span>
                </button>

                {/* Requeue button */}
                <button
                  onClick={() => requeueVisit?.(v.visit_id)}
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.3125rem 0.625rem', borderRadius: '0.375rem',
                    fontSize: '0.625rem', fontWeight: 600,
                    border: `1px solid ${isDark ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.2)'}`,
                    background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.06)',
                    color: isDark ? '#60a5fa' : '#2563eb',
                    transition: 'background 0.15s',
                  }}
                >
                  <span>🔄</span>
                  <span>Requeue</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog with mandatory comment */}
      {cancelConfirm && (
        <CancelConfirmDialog
          isDark={isDark}
          name={cancelConfirm.name}
          onConfirm={confirmCancelVisit}
          onCancel={() => setCancelConfirm(null)}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════
// TodayAppointmentsSection — Compact appointment list below queue
// ═══════════════════════════════════════════════════════════════════════


// TodayAppointmentsSection
function TodayAppointmentsSection({ isDark, onNavigateToAppointments }) {
  const { appointments, fetchList } = useAppointmentStore();
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      fetchList({ start_date: today, end_date: today, status: 'BOOKED' });
      setLoaded(true);
    }
  }, [loaded, fetchList]);

  // All BOOKED appointments sorted by time (upcoming first)
  const todayBooked = useMemo(() => {
    return (appointments || [])
      .filter((a) => a && a.status === 'BOOKED')
      .sort((a, b) => (a.appointment_time || '').localeCompare(b.appointment_time || ''));
  }, [appointments]);

  if (todayBooked.length === 0) return null;

  const INITIAL_COUNT = 5;
  const visibleAppts = expanded ? todayBooked : todayBooked.slice(0, INITIAL_COUNT);
  const hasMore = todayBooked.length > INITIAL_COUNT;

  const formatTime = (t) => {
    if (!t) return '';
    const m = /^(\d{1,2}):(\d{2})/.exec(t);
    if (!m) return t;
    const h = parseInt(m[1], 10);
    return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  return (
    <div
      style={{
        borderRadius: '0.5rem',
        border: `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)'}`,
        background: isDark ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.02)',
        overflow: 'hidden',
        cursor: onNavigateToAppointments ? 'pointer' : 'default',
      }}
      onClick={onNavigateToAppointments}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.5rem 0.75rem',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{ fontSize: '0.8125rem' }}>📅</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>
            Today's Appointments
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <span style={{
            fontSize: '0.625rem', fontWeight: 600, color: '#6366f1',
            background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
            padding: '0.0625rem 0.375rem', borderRadius: '0.25rem',
          }}>
            {todayBooked.length}
          </span>
          {onNavigateToAppointments && (
            <span style={{ fontSize: '0.6875rem', color: isDark ? '#a5b4fc' : '#6366f1' }}>→</span>
          )}
        </div>
      </div>
      <div style={{ padding: '0.25rem 0' }}>
        {visibleAppts.map((appt) => (
          <div key={appt.appointment_id} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.375rem 0.75rem',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'}`,
          }}>
            <span style={{
              fontSize: '0.6875rem', fontWeight: 700, color: '#6366f1',
              fontFamily: 'monospace', minWidth: '1.5rem',
            }}>
              #{appt.appointment_number}
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: 500, color: isDark ? '#f5f5f7' : '#111827', flex: 1 }}>
              {appt.patient_name}
            </span>
            {appt.patient_mobile && (
              <PhoneButton phoneNumber={appt.patient_mobile} isDark={isDark} size="sm" />
            )}
            <span style={{ fontSize: '0.625rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
              {formatTime(appt.appointment_time)}
            </span>
            <span style={{ fontSize: '0.5625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
              Dr. {(appt.doctor_name || '').split(' ')[0]}
            </span>
          </div>
        ))}
        {hasMore && (
          <button
            onClick={() => setExpanded((e) => !e)}
            style={{
              width: '100%', padding: '0.375rem',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.6875rem', fontWeight: 600,
              color: isDark ? '#a5b4fc' : '#6366f1',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
            }}
          >
            {expanded
              ? '▲ Show less'
              : `▼ Load more (${todayBooked.length - INITIAL_COUNT})`}
          </button>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// Shared sub-components
// ═══════════════════════════════════════════════════════════════════════
